// Desktop (.exe) Settings page: account, language, update, offline map database.
import { Settings as SettingsIcon, LogOut, Database, RefreshCw } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { CheckUpdateButton } from "../components/UpdateBanner";
import { downloadBasemap, installedBasemapVersion, checkDbUpdate } from "../flashtools/lib/mapdb";

export default function DesktopSettings() {
  const { user, isAdmin, logout } = useAuth();
  const app = typeof window !== "undefined" ? window.tiro : null;
  const [progress, setProgress] = useState(-1);
  const [dbVer, setDbVer] = useState(installedBasemapVersion());
  const [latest, setLatest] = useState(null);

  const checkDb = async () => {
    const r = await checkDbUpdate(api);
    if (!r) { toast.error("Tidak bisa cek Library (offline)"); return; }
    setLatest(r);
    if (!r.latest) toast.info("Library belum dirilis admin");
    else if (r.outdated) toast.success(`Library v${r.latest} tersedia`);
    else toast.success("Library sudah versi terbaru");
  };

  const updateDb = async (part) => {
    setProgress(0);
    try {
      const d = await downloadBasemap(api, setProgress, part);
      setDbVer(d.version); setLatest(null);
      const label = part === "map" ? "Basemap" : part === "ecu" ? "Info Bike" : "Library";
      toast.success(`${label} v${d.version} terpasang — ${d.added} entri`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal mengunduh");
    } finally { setProgress(-1); }
  };

  const Row = ({ label, children }) => (
    <div className="flex items-center justify-between gap-4 py-3 border-b border-white/5 last:border-0">
      <span className="text-sm text-slate-400">{label}</span><div className="text-sm text-slate-200 flex items-center gap-2">{children}</div>
    </div>
  );

  return (
    <div className="p-6 sm:p-8 max-w-4xl" data-testid="settings-page">
      <div className="flex items-center gap-5 mb-8 border-l-[3px] border-[#e52321] pl-5">
        <SettingsIcon className="h-9 w-9 text-[#e52321]" />
        <div><h1 className="text-[26px] font-black tracking-wide text-white uppercase">Settings</h1><p className="text-sm text-slate-400">Account, language and update.</p></div>
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        <section className="rounded-xl border border-[#2a2a2a] bg-[#181818] p-5">
          <h2 className="text-base font-bold text-white mb-2">Account</h2>
          <Row label="Name">{user?.name || "—"}</Row>
          <Row label="Email">{user?.email || "—"}</Row>
          <Row label="Role">{isAdmin ? "Super Admin" : "User"}</Row>
          <Row label="Status"><span className={user?.status === "active" ? "text-emerald-400" : "text-amber-400"}>{user?.status || "—"}</span></Row>
          <Row label="License">{user?.license_expired ? <span className="text-red-400">Expired</span> : (user?.license_until || "Active")}</Row>
          <button data-testid="settings-logout" onClick={logout} className="mt-4 w-full h-10 rounded-lg border border-red-500/40 bg-red-600/10 text-red-300 text-sm font-bold flex items-center justify-center gap-2 hover:bg-red-600/20"><LogOut className="h-4 w-4" /> Sign out</button>
        </section>
        <section className="rounded-xl border border-[#2a2a2a] bg-[#181818] p-5">
          <h2 className="text-base font-bold text-white mb-2">Language & Update</h2>
          <Row label="Language"><LanguageSwitcher /></Row>
          <Row label="App version">{app?.appVersion || "web"}</Row>
          <Row label="HWID"><span className="font-mono text-xs">{app?.hwid || "—"}</span></Row>
          <div className="mt-4"><CheckUpdateButton /></div>
        </section>
        <section className="rounded-xl border border-[#2a2a2a] bg-[#181818] p-5">
          <h2 className="text-base font-bold text-white mb-2 flex items-center gap-2"><Database className="h-4 w-4 text-[#e52321]" /> Map & Library</h2>
          <Row label="Installed version"><span className="font-mono text-xs">{dbVer ? `v${dbVer}` : "none"}</span></Row>
          {latest?.latest && <Row label="Latest release"><span className={`font-mono text-xs ${latest.outdated ? "text-amber-400" : "text-emerald-400"}`}>v{latest.latest}</span></Row>}
          {progress >= 0 && (
            <div className="mt-4" data-testid="settings-db-progress">
              <div className="h-2 w-full rounded bg-[#2a2a2a] overflow-hidden"><div className="h-full bg-[#e52321] transition-[width]" style={{ width: `${progress}%` }} /></div>
              <div className="text-xs text-slate-400 mt-1 font-mono">Downloading… {progress}%</div>
            </div>
          )}
          <div className="mt-4 space-y-2">
            <button data-testid="settings-check-db" onClick={checkDb} disabled={progress >= 0}
              className="w-full h-10 rounded-lg bg-[#2a2a2a] hover:bg-[#333] text-slate-200 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
              <RefreshCw className="h-4 w-4" /> Check for Update
            </button>
            <div className="grid grid-cols-2 gap-2">
              <button data-testid="settings-update-map" onClick={() => updateDb("map")} disabled={progress >= 0}
                className="h-10 rounded-lg bg-[#e52321] hover:bg-[#c51717] text-white text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50">
                <Database className="h-4 w-4" /> {progress >= 0 ? "…" : "Update Basemap"}
              </button>
              <button data-testid="settings-update-ecu" onClick={() => updateDb("ecu")} disabled={progress >= 0}
                className="h-10 rounded-lg bg-[#2a2a2a] hover:bg-[#333] border border-[#e52321]/40 text-slate-100 text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50">
                <Database className="h-4 w-4" /> {progress >= 0 ? "…" : "Update Info Bike"}
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
